<!--
  NonIntegratedSuppliers -- SuperAdmin list + editor for the suppliers
  ThreadFlow does NOT integrate with.

  Jake's IA decision, 2026-08-26 (docs/planning/
  2026-08-26-sku-setup-review-gaps.md), verbatim: "I think non-integrated
  suppliers is what belongs under Tools, and SKU setup is a sub-option
  there. SKU is a thing that lives under a supplier, not a top-level
  concept."

  So this page is the PARENT of the SKU setup page. The route it links
  to carries a supplier id, replacing the old
  `/admin/suppliers/local/sku-setup`, where the literal `local` segment
  named a CLASS in the position an IDENTITY belongs.

  SCOPE: NON-INTEGRATED SUPPLIERS ONLY
  ====================================
  Every row here has `is_smart_supplier = FALSE`. SanMar and S&S are
  configured through integration credentials, never through this form,
  and the filter is enforced by the ENDPOINT rather than by a query
  parameter -- so no bug on this page can widen the set. See
  `api/src/endpoints/vendor.py`.

  That is not a tidiness rule. `vendors.supplier_key` is an EXTERNAL
  CONTRACT field: the value must match a key in the back office's
  suppliers table, an unknown key 422s the WHOLE order, and an omitted
  one is silently defaulted to SanMar. Putting a smart supplier's
  identity behind an editable text box is how a typo breaks every order
  to a partner.

  WHY THE FORM STATES THE CONSEQUENCE
  ===================================
  A non-integrated supplier can only carry a supplier key once one is
  mapped with the back office (phase 4). Until then its orders will not
  submit -- Jake's F3b decision is that they BLOCK rather than being
  emitted with the field omitted, because omitted means SanMar. A form
  that showed the field with no explanation would make an unmapped
  supplier look finished. The note beside the field says otherwise.

  Endpoints
  =========
  * GET   /api/suppliers/non-integrated
  * POST  /api/suppliers/non-integrated
  * PATCH /api/suppliers/non-integrated/<id>

  data-testid prefix: sa-suppliers-*
-->
<template>
    <section
        class="ni-suppliers"
        data-testid="sa-suppliers-root"
    >
        <header class="ni-suppliers__header">
            <h2 class="ni-suppliers__title">
                Non-integrated suppliers
            </h2>
            <button
                type="button"
                class="ni-suppliers__btn ni-suppliers__btn--primary"
                data-testid="sa-suppliers-add"
                @click="startCreate"
            >
                + Add supplier
            </button>
        </header>

        <p class="ni-suppliers__lede">
            Suppliers we buy blank goods from but do not integrate with.
            Their catalog is not fetched, and their previews are
            composited by ThreadFlow rather than by Artifi -- which is
            what the per-SKU setup below each supplier produces.
        </p>

        <p class="ni-suppliers__note">
            <strong>SanMar and S&amp;S are not listed here.</strong>
            Integrated suppliers are configured through their
            integration credentials, not through this form.
        </p>

        <div
            v-if="loading"
            data-testid="sa-suppliers-loading"
        >
            Loading...
        </div>

        <p
            v-else-if="!suppliers.length"
            data-testid="sa-suppliers-empty"
        >
            No non-integrated suppliers yet.
        </p>

        <!--
          `sa-suppliers-table` is the LOADED branch and `-empty` above is
          its sibling. Asserting the pair is what distinguishes "the list
          came back with rows" from "the page mounted and rendered
          chrome" -- a single always-present container would prove
          neither (the empty-page trap that let a batch scenario pass on
          a store with zero batches).
        -->
        <table
            v-else
            class="ni-suppliers__table"
            data-testid="sa-suppliers-table"
        >
            <thead>
                <tr>
                    <th>Supplier</th>
                    <th>Email</th>
                    <th>Supplier key</th>
                    <th />
                </tr>
            </thead>
            <tbody>
                <!--
                  `data-index` carries the ROW POSITION alongside the
                  id-keyed testid. A scenario cannot hardcode a supplier
                  id: which vendors exist is a SEED detail that differs
                  between sandboxes (the pseudo-supplier is id 1 on a
                  current seed and absent on an older one), so an
                  id-keyed selector tests the fixture rather than the
                  page. `sa-suppliers-first-*` below is the durable
                  anchor; the id-keyed ones stay for targeting a KNOWN
                  supplier when a test genuinely means a specific row.
                -->
                <tr
                    v-for="(supplier, index) in suppliers"
                    :key="supplier.id"
                    :data-index="index"
                    :data-testid="`sa-suppliers-row-${supplier.id}`"
                >
                    <td>{{ supplier.name }}</td>
                    <td>{{ supplier.email }}</td>
                    <td>
                        <!--
                          The unmapped state is CALLED OUT, not left
                          blank. A blank cell reads as "nothing to see";
                          this reads as "this supplier cannot ship yet",
                          which is what it means.
                        -->
                        <span
                            v-if="supplier.supplier_key"
                            :data-testid="`sa-suppliers-key-${supplier.id}`"
                        >{{ supplier.supplier_key }}</span>
                        <!--
                          A row that PREDATES the requirement. Called out
                          as REPAIRABLE, not merely broken: the key is
                          required for new suppliers as of 2026-08-29, but
                          nothing backfills these rows, because a
                          fabricated key rejects an entire order at the
                          back office while a NULL merely fails to submit.
                          The only fix is a human supplying the real key,
                          so the prompt points at Edit.
                        -->
                        <span
                            v-else
                            class="ni-suppliers__needs-key"
                            :data-testid="`sa-suppliers-needs-key-${supplier.id}`"
                        >Key needed -- not on any plan, and orders will not
                            submit. Edit to add it.</span>
                    </td>
                    <td class="ni-suppliers__row-actions">
                        <button
                            type="button"
                            class="ni-suppliers__btn"
                            :data-testid="`sa-suppliers-edit-${supplier.id}`"
                            @click="startEdit(supplier)"
                        >
                            Edit
                        </button>
                        <router-link
                            class="ni-suppliers__btn"
                            :to="`/admin/suppliers/non-integrated/${supplier.id}/sku-setup`"
                            :data-testid="
                                index === 0
                                    ? 'sa-suppliers-first-open-sku-setup'
                                    : `sa-suppliers-open-sku-setup-${supplier.id}`
                            "
                        >
                            SKU setup
                        </router-link>
                        <button
                            type="button"
                            class="ni-suppliers__btn ni-suppliers__btn--danger"
                            :data-testid="
                                index === 0
                                    ? 'sa-suppliers-first-delete'
                                    : `sa-suppliers-delete-${supplier.id}`
                            "
                            @click="startDelete(supplier)"
                        >
                            Delete
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Create / edit form. One form for both, because the fields
             are identical and a second component would be a second
             place for the supplier_key note to drift out of. -->
        <form
            v-if="draft"
            class="ni-suppliers__form"
            data-testid="sa-suppliers-form"
            @submit.prevent="save"
        >
            <h3 class="ni-suppliers__form-title">
                {{ draft.id ? "Edit supplier" : "New supplier" }}
            </h3>

            <label class="ni-suppliers__field">
                <span>Name</span>
                <input
                    v-model="draft.name"
                    type="text"
                    data-testid="sa-suppliers-field-name"
                >
            </label>

            <label class="ni-suppliers__field">
                <span>Email</span>
                <input
                    v-model="draft.email"
                    type="text"
                    data-testid="sa-suppliers-field-email"
                >
            </label>

            <label class="ni-suppliers__field">
                <span>Address</span>
                <input
                    v-model="draft.address"
                    type="text"
                    data-testid="sa-suppliers-field-address"
                >
            </label>

            <label class="ni-suppliers__field">
                <span>Supplier key (required)</span>
                <input
                    v-model="draft.supplier_key"
                    type="text"
                    data-testid="sa-suppliers-field-supplier-key"
                >
            </label>

            <!--
              The REQUIRED message, distinct from both the static note and
              the unmapped warning below it. Rendered only after a submit
              attempt, so an SA who has not finished typing is not scolded
              mid-form -- but the Save button is disabled from the start,
              so the state is visible without being noisy.
            -->
            <p
                v-if="showSupplierKeyRequired"
                class="ni-suppliers__key-required"
                data-testid="sa-suppliers-supplier-key-required"
            >
                <strong>A supplier key is required.</strong>
                Enter the key the back office knows this supplier by. It
                is the value ThreadFlow sends with every order, and it is
                also what puts this supplier on a subscription plan --
                without it the supplier cannot appear on any plan at all.
                Do not guess: a key the back office does not recognise
                rejects the entire order.
            </p>

            <!--
              The consequence, stated next to the field that causes it
              (Jake, 2026-08-26). Not a tooltip: someone filling this
              form in needs to know BEFORE they leave it that an
              unmapped supplier is not order-ready.
            -->
            <p
                class="ni-suppliers__key-note"
                data-testid="sa-suppliers-supplier-key-note"
            >
                Any well-formed key is accepted here -- a supplier does
                not have to be one ThreadFlow already integrates with.
                The key only becomes order-ready once the back office
                has it too.
            </p>

            <!--
              The LIVE state, distinct from the static note above. The
              note explains the rule; this fires only when THIS key is
              in the unmapped state, so it carries information rather
              than being wallpaper.

              Warning, not error, and deliberately non-blocking: saving
              an unmapped supplier is legitimate and expected (Jake,
              2026-08-26). What it must not be is a SURPRISE later --
              the order-emission guard refuses to submit any line
              carrying an unmapped key, and this makes that refusal
              predictable at the moment the key is typed.
            -->
            <p
                v-if="supplierKeyIsUnmapped"
                class="ni-suppliers__key-warning"
                data-testid="sa-suppliers-supplier-key-unmapped"
            >
                <strong>Not order-ready.</strong>
                The back office does not yet recognise
                <code>{{ normalizedSupplierKey }}</code>. You can save
                this supplier, but its orders will not submit until the
                key is confirmed on the back-office side -- a key they
                do not know rejects the entire order, not just its own
                line.
            </p>

            <div class="ni-suppliers__form-actions">
                <button
                    type="submit"
                    class="ni-suppliers__btn ni-suppliers__btn--primary"
                    :disabled="saving || !normalizedSupplierKey"
                    data-testid="sa-suppliers-save"
                >
                    {{ saving ? "Saving..." : "Save" }}
                </button>
                <button
                    type="button"
                    class="ni-suppliers__btn"
                    data-testid="sa-suppliers-cancel"
                    @click="draft = null"
                >
                    Cancel
                </button>
            </div>
        </form>

        <!-- THE WARNING. Jake asked for a "very prominent" one, and
             prominence here means SPECIFIC: it names the supplier, it
             names what still points at it (with counts, from the server),
             and it says plainly that the act cannot be undone. A confirm
             that does not say what it destroys is theatre. -->
        <div
            v-if="pendingDelete"
            class="ni-suppliers__danger"
            data-testid="sa-suppliers-delete-confirm"
        >
            <h3 class="ni-suppliers__danger-title">
                Delete {{ pendingDelete.name }}?
            </h3>

            <p v-if="deletePreviewLoading" data-testid="sa-suppliers-delete-checking">
                Checking what still references this supplier&hellip;
            </p>

            <template v-else-if="deleteReferences.length">
                <p>
                    <strong>{{ pendingDelete.name }} cannot be deleted.</strong>
                    It is still referenced by:
                </p>
                <ul data-testid="sa-suppliers-delete-references">
                    <li
                        v-for="ref in deleteReferences"
                        :key="ref.table"
                        :data-testid="`sa-suppliers-delete-ref-${ref.table}`"
                    >
                        {{ ref.count }} {{ ref.label }}
                    </li>
                </ul>
                <p>
                    Reassign or remove those first. Deleting it anyway would
                    leave them pointing at a supplier that no longer exists.
                </p>
            </template>

            <template v-else>
                <p>
                    This permanently removes <strong>{{ pendingDelete.name }}</strong>
                    from the platform catalog. <strong>This cannot be undone.</strong>
                </p>
                <p>
                    Nothing currently references this supplier, so no products,
                    campaigns or past orders change. Past orders record their
                    supplier separately and are not affected either way.
                </p>
            </template>

            <div class="ni-suppliers__danger-actions">
                <button
                    v-if="!deletePreviewLoading && !deleteReferences.length"
                    type="button"
                    class="ni-suppliers__btn ni-suppliers__btn--danger"
                    :disabled="deleting"
                    data-testid="sa-suppliers-delete-confirm-yes"
                    @click="confirmDelete()"
                >
                    {{ deleting ? "Deleting..." : `Delete ${pendingDelete.name}` }}
                </button>
                <button
                    type="button"
                    class="ni-suppliers__btn"
                    data-testid="sa-suppliers-delete-confirm-no"
                    @click="cancelDelete()"
                >
                    Cancel
                </button>
            </div>
        </div>

        <p
            v-if="errorMsg"
            class="ni-suppliers__error"
            data-testid="sa-suppliers-error"
        >
            {{ errorMsg }}
        </p>

        <p
            v-if="successMsg"
            class="ni-suppliers__success"
            data-testid="sa-suppliers-success"
        >
            {{ successMsg }}
        </p>
    </section>
</template>

<script>
import axios from "@/axios";

const BASE = "/api/suppliers/non-integrated";

/**
 * Keys the BACK OFFICE has confirmed, mirroring the backend's
 * `OUR_KEY_TO_THEIR_KEY` (api/src/integrations/deconetwork/serializers.py).
 *
 * WHY A MIRROR AND NOT A FETCH
 * ----------------------------
 * This drives a warning, never a decision. The authoritative check lives
 * at order emission, where `_supplier_key_for` marks an unmapped line and
 * `_validate_order_payload` refuses the submission -- so if this list
 * drifts, the worst case is a warning that is late or spurious on an
 * admin form, never an order that goes out wrong. Adding a route to
 * serve one hardcoded string would be machinery for no additional
 * safety.
 *
 * The list is deliberately SHORT and expected to stay so: widening
 * `OUR_KEY_TO_THEIR_KEY` is a coordinated act with Dustin (phase 4),
 * because an unknown key 422s the WHOLE order at tf-deconetwork. When
 * that happens, this array changes in the same PR.
 */
const BACK_OFFICE_MAPPED_KEYS = ["sanmar"];

export default {
    name: "NonIntegratedSuppliers",
    data() {
        return {
            suppliers: [],
            // null when no form is open. Holds `id` when editing.
            draft: null,
            loading: false,
            saving: false,
            // True once Save has been pressed on the OPEN draft. Gates the
            // required-key message so a half-typed form is not scolded,
            // while the disabled Save button still shows the state.
            // Reset by startCreate/startEdit, so it never leaks between
            // one supplier's form and the next.
            submitAttempted: false,
            errorMsg: "",
            successMsg: "",
            // The supplier whose delete is being confirmed, or null.
            pendingDelete: null,
            // What the SERVER says still points at it. Never derived
            // locally -- this page does not know about products or
            // campaigns, and a guess here would understate the damage.
            deleteReferences: [],
            deletePreviewLoading: false,
            deleting: false,
        };
    },
    computed: {
        /**
         * The key as the BACKEND will store it.
         *
         * `Vendor.validate_supplier_key` applies `.strip().lower()`, so
         * "  SanMar " persists as "sanmar" and IS mapped. Comparing the
         * raw input would warn about a supplier that is fully
         * order-ready -- a false alarm, which is the fastest way to
         * teach people to ignore this warning where it matters.
         */
        normalizedSupplierKey() {
            return (this.draft?.supplier_key || "").trim().toLowerCase();
        },
        /**
         * True only when a key is PRESENT and the back office has not
         * confirmed it.
         *
         * Blank is excluded on purpose: it is the correct, permanent
         * state of every ordinary local vendor, not a problem to flag.
         */
        supplierKeyIsUnmapped() {
            const key = this.normalizedSupplierKey;
            if (!key) return false;
            return !BACK_OFFICE_MAPPED_KEYS.includes(key);
        },
        /**
         * Whether to show the REQUIRED message.
         *
         * Reuses `normalizedSupplierKey`, so "   " counts as missing by
         * the same `.trim().toLowerCase()` the BACKEND applies -- the
         * form and `Vendor.validate_supplier_key` must agree on what
         * "blank" means, or the form would let through a value the
         * endpoint then 400s.
         *
         * The server is the actual gate (`_require_supplier_key` in
         * api/src/endpoints/vendor.py). This only makes the refusal
         * predictable before the round trip.
         */
        showSupplierKeyRequired() {
            return this.submitAttempted && !this.normalizedSupplierKey;
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        /**
         * Open the warning. DOES NOT DELETE.
         *
         * The first click is a question, never the act: deletion here is
         * irreversible and the page cannot see what it would break, so
         * the preview fetch below is what turns "are you sure" into a
         * statement of consequence.
         */
        async startDelete(supplier) {
            this.pendingDelete = supplier;
            this.deleteReferences = [];
            this.errorMsg = "";
            this.successMsg = "";
            this.deletePreviewLoading = true;
            try {
                const resp = await axios.get(
                    `${BASE}/${supplier.id}/delete-preview`
                );
                this.deleteReferences = resp.data?.references || [];
            } catch (err) {
                // FAIL CLOSED. If we cannot find out what this would
                // break, we must not offer to do it -- an empty
                // reference list would otherwise read as "safe" when it
                // actually means "unknown".
                this.deleteReferences = [
                    {
                        table: "__unknown__",
                        label:
                            "unknown references (the check failed, so this " +
                            "cannot be deleted safely)",
                        count: "?",
                    },
                ];
                this.errorMsg = this.readError(err);
            } finally {
                this.deletePreviewLoading = false;
            }
        },
        cancelDelete() {
            this.pendingDelete = null;
            this.deleteReferences = [];
            this.deletePreviewLoading = false;
        },
        async confirmDelete() {
            if (!this.pendingDelete) return;
            const supplier = this.pendingDelete;
            this.deleting = true;
            this.errorMsg = "";
            try {
                await axios.delete(`${BASE}/${supplier.id}`);
                this.successMsg = `${supplier.name} was deleted.`;
                this.pendingDelete = null;
                this.deleteReferences = [];
                // Refetch rather than splicing locally: the server is
                // the authority on what remains, and a 409 that raced us
                // must not leave the row hidden while it still exists.
                await this.fetch();
            } catch (err) {
                this.errorMsg = this.readError(err);
                // A refused delete (409) means something started
                // referencing it since the preview. Show the new reason
                // rather than the stale "safe to delete" panel.
                const refs = err?.response?.data?.references;
                if (Array.isArray(refs) && refs.length) {
                    this.deleteReferences = refs;
                }
            } finally {
                this.deleting = false;
            }
        },
        readError(err) {
            return (
                err?.response?.data?.error ||
                err?.message ||
                "Something went wrong."
            );
        },
        async fetch() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await axios.get(BASE);
                this.suppliers = resp.data.suppliers || [];
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error ||
                    "Failed to load suppliers.";
            } finally {
                this.loading = false;
            }
        },
        startCreate() {
            this.successMsg = "";
            this.errorMsg = "";
            this.submitAttempted = false;
            this.draft = {
                id: null,
                name: "",
                email: "",
                address: "",
                supplier_key: "",
            };
        },
        startEdit(supplier) {
            this.successMsg = "";
            this.errorMsg = "";
            this.submitAttempted = false;
            this.draft = {
                id: supplier.id,
                name: supplier.name || "",
                email: supplier.email || "",
                address: supplier.address || "",
                supplier_key: supplier.supplier_key || "",
            };
        },
        async save() {
            if (!this.draft) return;

            this.submitAttempted = true;
            // REFUSE LOCALLY, before any request. The endpoint refuses
            // this too (a 400 from `_require_supplier_key`), and that is
            // the real gate -- it holds for callers that are not this
            // form. Stopping here only means the SA reads a message about
            // the field they missed instead of a failed save.
            //
            // Checked on EDIT as well as create: a rule enforced only on
            // create is a rule that lasts until the first edit.
            if (!this.normalizedSupplierKey) {
                this.errorMsg = "";
                this.successMsg = "";
                return;
            }

            this.saving = true;
            this.errorMsg = "";
            this.successMsg = "";

            const body = {
                name: this.draft.name,
                email: this.draft.email,
                address: this.draft.address,
                supplier_key: this.draft.supplier_key,
            };

            try {
                if (this.draft.id) {
                    await axios.patch(`${BASE}/${this.draft.id}`, body);
                } else {
                    await axios.post(BASE, body);
                }
                this.draft = null;
                this.successMsg = "Saved.";
                // Re-read rather than patching the local array, so the
                // list settles to exactly what the backend stored --
                // including a supplier_key its validator normalized.
                await this.fetch();
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error || "Failed to save supplier.";
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.ni-suppliers {
    padding: 24px;
}

.ni-suppliers__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.ni-suppliers__title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
}

.ni-suppliers__lede,
.ni-suppliers__note {
    max-width: 70ch;
    margin: 12px 0;
    color: #4a5560;
}

.ni-suppliers__table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 16px;
}

.ni-suppliers__table th,
.ni-suppliers__table td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid #e3e7eb;
}

.ni-suppliers__row-actions {
    display: flex;
    gap: 8px;
}

/* A legacy row missing its key. Red rather than the amber used for an
   unmapped-but-present key: this one is not merely un-submittable, it is
   also invisible to every subscription plan, and it is the SA's to fix. */
.ni-suppliers__needs-key {
    color: #a12b1f;
}

.ni-suppliers__btn {
    border: 1px solid #c9d3db;
    border-radius: 6px;
    background: #fff;
    padding: 6px 12px;
    cursor: pointer;
    font: inherit;
    text-decoration: none;
    color: inherit;
}
/* Destructive actions read as destructive BEFORE they are clicked.
   Colour is a hint, never the guard -- the guard is the confirm panel,
   which states the consequence in words. */
.ni-suppliers__btn--danger {
    border-color: #c0392b;
    color: #c0392b;
}
.ni-suppliers__btn--danger:disabled {
    opacity: 0.6;
    cursor: default;
}
/* The warning panel. Deliberately heavy: a full-width bordered block
   rather than an inline sentence, because it interrupts. */
.ni-suppliers__danger {
    margin: 16px 0;
    padding: 16px;
    border: 2px solid #c0392b;
    border-radius: 8px;
    background: #fdf3f2;
}
.ni-suppliers__danger-title {
    margin: 0 0 8px;
    color: #c0392b;
}
.ni-suppliers__danger-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}

.ni-suppliers__btn--primary {
    background: #22364f;
    border-color: #22364f;
    color: #fff;
}

.ni-suppliers__form {
    margin-top: 24px;
    max-width: 48ch;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ni-suppliers__form-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
}

.ni-suppliers__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.ni-suppliers__field input {
    border: 1px solid #c9d3db;
    border-radius: 6px;
    padding: 8px 10px;
    font: inherit;
}

.ni-suppliers__key-note {
    color: #4a5560;
    margin: 0;
}

/* Amber, not red: saving an unmapped supplier is legitimate. The colour
   has to read as "know this" rather than "you did something wrong", or
   people will try to make it go away by clearing a key they need. */
.ni-suppliers__key-warning {
    margin: 8px 0 0;
    padding: 10px 12px;
    border-left: 3px solid #b26a00;
    background: #fff6e5;
    color: #5c3a00;
}

.ni-suppliers__key-required {
    margin: 8px 0 0;
    padding: 10px 12px;
    border-left: 3px solid #a12b1f;
    background: #fdecea;
    color: #6b1a12;
}

.ni-suppliers__key-warning code {
    background: #ffe9c2;
    padding: 0 4px;
    border-radius: 3px;
}

.ni-suppliers__form-actions {
    display: flex;
    gap: 8px;
}

.ni-suppliers__error {
    color: #b3261e;
}

.ni-suppliers__success {
    color: #1b5e20;
}
</style>
